import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";

interface AuthContextType {
  token: string | null;
  isLoading: boolean;
  getToken: () => Promise<string>;
  refreshToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH0_DOMAIN = "dev-ociu84lf8keizm5i.us.auth0.com";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: auth0Loading,
  } = useAuth0();
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isRefreshingRef = useRef<Promise<string> | null>(null);

  // Fetch a fresh token from Auth0
  const fetchFreshToken = useCallback(async (): Promise<string> => {
    if (!isAuthenticated) {
      throw new Error("User is not authenticated");
    }

    // Prevent multiple simultaneous refresh attempts
    if (isRefreshingRef.current) {
      return isRefreshingRef.current;
    }

    try {
      const refreshPromise = getAccessTokenSilently({
        authorizationParams: {
          audience: `https://${AUTH0_DOMAIN}/api/v2/`,
          scope: "openid profile email read:current_user",
        },
        cacheMode: "off", // Force fresh token
      });

      isRefreshingRef.current = refreshPromise;
      const accessToken = await refreshPromise;

      setToken(accessToken);
      localStorage.setItem("auth0_access_token", accessToken);

      return accessToken;
    } catch (error) {
      console.error("Error fetching access token:", error);
      setToken(null);
      localStorage.removeItem("auth0_access_token");
      throw error;
    } finally {
      isRefreshingRef.current = null;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Initial token fetch on mount and handle logout
  useEffect(() => {
    const initToken = async () => {
      if (isAuthenticated && !auth0Loading) {
        try {
          // Try to get cached token from localStorage first
          const cachedToken = localStorage.getItem("auth0_access_token");
          if (cachedToken) {
            // Use cached token - if it's expired, we'll get a 401 and refresh then
            setToken(cachedToken);
            setIsLoading(false);
            return;
          }
          // Fetch fresh token if no cached token
          await fetchFreshToken();
        } catch (error) {
          console.error("Error initializing token:", error);
        } finally {
          setIsLoading(false);
        }
      } else if (!auth0Loading) {
        // User is not authenticated - clear cached token
        setToken(null);
        localStorage.removeItem("auth0_access_token");
        setIsLoading(false);
      }
    };

    initToken();
  }, [isAuthenticated, auth0Loading, fetchFreshToken]);

  // Get token - just return current token, no expiry checking
  const getToken = useCallback(async (): Promise<string> => {
    if (!isAuthenticated) {
      throw new Error("User is not authenticated");
    }
    if (token) {
      return token;
    }
    // If no token, fetch one
    return await fetchFreshToken();
  }, [token, fetchFreshToken, isAuthenticated]);

  // Force refresh token (called after 401 response)
  const refreshToken = useCallback(async (): Promise<string> => {
    if (!isAuthenticated) {
      throw new Error("User is not authenticated");
    }
    return await fetchFreshToken();
  }, [fetchFreshToken, isAuthenticated]);

  return (
    <AuthContext.Provider value={{ token, isLoading, getToken, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthToken = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthToken must be used within an AuthProvider");
  }
  return context;
};
